/**
 * ModelsPage — model scorecards and the routing interface.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05B. Consumes C02D's model
 * scorecards (via `GET /v1/evals/scorecards?dimension=model`) and routing
 * decisions (via `POST /v1/evals/routing`) — both served by
 * `auteur_cloud/api/routes/evals.py`, see `api/evals.ts`.
 *
 * The required property this page exists to preserve: **recommendations do
 * not silently change exact user requests.** The mechanics live server-side
 * (`RoutingDecision.__post_init__` raises `SilentSubstitutionRefused` rather
 * than constructing such a decision); this page's job is to render the
 * decision faithfully:
 *
 * - The decision card always shows what was requested, what will run
 *   (`applied_model`), and whether that differs (`substituted`). For an
 *   exact request those two are always the same model, by construction.
 * - An advisory is rendered as a labelled suggestion with its rationale and
 *   margin — never applied by this page, never re-worded into a decision.
 * - `insufficient_evidence` renders as stated, not as a guess.
 * - Non-model subjects cannot appear here: the request is always
 *   `dimension=model`, and C02D's read predicate (`subject_kind = 'model'`)
 *   keeps executor/prompt/repository rows out of the model leaderboard.
 *
 * Build status: compiled and exercised as part of the Vite customer SPA.
 */

import { useEffect, useState } from 'react';
import {
  fetchRoutingDecision,
  fetchScorecards,
  type RoutingDecisionPayload,
  type ScorecardPayload,
} from '../../api/evals';

const STATUS_COPY: Record<string, string> = {
  insufficient_evidence:
    'Not enough receipts to recommend. No advisory is published below the minimum sample threshold.',
  already_best: 'The requested model already leads on the available scorecards.',
  within_margin:
    'The candidates differ by less than the recommendation margin. No switch suggested.',
  advisory_only:
    'Suggestion only — the requested model still runs. Nothing here changes an exact request.',
  selected: 'The product-selected model will run (this was not an exact request).',
};

export function ModelsPage() {
  const [cards, setCards] = useState<ScorecardPayload[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [requestedModel, setRequestedModel] = useState('');
  const [exact, setExact] = useState(true);
  const [decision, setDecision] = useState<RoutingDecisionPayload | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchScorecards('model')
      .then((result) => {
        if (!cancelled) setCards(result);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ask = () => {
    if (!requestedModel.trim()) {
      return;
    }
    setDecision(null);
    setDecisionError(null);
    fetchRoutingDecision(requestedModel.trim(), exact)
      .then((result) => setDecision(result))
      .catch(() =>
        setDecisionError(
          'Unavailable — the routing service is not reachable from this deployment yet.',
        ),
      );
  };

  return (
    <section
      className="models-page"
      data-state={unavailable ? 'unavailable' : cards ? 'ready' : 'loading'}
    >
      <h1>Models</h1>

      {unavailable ? (
        <p>
          Unavailable — the evals service is not reachable from this deployment
          yet. No leaderboard is fabricated.
        </p>
      ) : cards === null ? (
        <p>Loading model scorecards…</p>
      ) : cards.length === 0 ? (
        <p>
          No model receipts yet. An empty leaderboard is not a ranking of zero.
        </p>
      ) : (
        <table className="scorecard-table">
          <thead>
            <tr>
              <th>Model</th>
              <th>Samples</th>
              <th>Normalized mean</th>
              <th>Trusted-green rate</th>
              <th>Cost per trusted result</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((card) => (
              <tr key={`${card.subject_key}/${card.slice}`}>
                <td>{card.subject_key}</td>
                <td>{card.sample_count}</td>
                <td>
                  {card.normalized_score_mean === null
                    ? 'Unavailable'
                    : card.normalized_score_mean.toFixed(3)}
                </td>
                <td>
                  {card.trusted_green_rate === null
                    ? 'Unavailable'
                    : `${(card.trusted_green_rate * 100).toFixed(1)}%`}
                </td>
                <td>
                  {card.cost_per_trusted_green_usd === null
                    ? 'Unavailable'
                    : `$${card.cost_per_trusted_green_usd.toFixed(4)}`}
                </td>
                <td>{card.evidence_weight}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Routing</h2>
      <div className="routing-form">
        <label>
          Requested model
          <input
            type="text"
            value={requestedModel}
            onChange={(event) => setRequestedModel(event.target.value)}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={exact}
            onChange={(event) => setExact(event.target.checked)}
          />
          Exact request (run exactly what is named)
        </label>
        <button type="button" onClick={ask}>
          Preview decision
        </button>
      </div>

      {decisionError ? <p>{decisionError}</p> : null}

      {decision ? (
        <div className="routing-decision" data-status={decision.status}>
          <p>
            Requested: <strong>{decision.requested_model}</strong>
            {decision.exact_request ? ' (exact)' : ' (product may choose)'}
          </p>
          <p>
            Will run: <strong>{decision.applied_model}</strong>
            {decision.substituted ? ' — substituted' : ''}
          </p>
          <p>{STATUS_COPY[decision.status] || decision.status}</p>
          {decision.reasons.length > 0 ? (
            <ul>
              {decision.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
          {decision.advisory ? (
            <aside className="routing-advisory">
              <h3>Suggestion (advisory only — dismissable)</h3>
              <p>
                {decision.advisory.subject_key}: normalized{' '}
                {decision.advisory.normalized_score.toFixed(3)}, margin{' '}
                {decision.advisory.margin.toFixed(3)}, evidence{' '}
                {decision.advisory.evidence_weight} ({decision.advisory.sample_count}{' '}
                samples)
              </p>
              <p>{decision.advisory.rationale}</p>
            </aside>
          ) : null}
        </div>
      ) : null}

      <p className="page-note">
        An exact request always runs the requested model; any alternative is a
        labelled, dismissable suggestion. This page applies nothing itself.
      </p>
    </section>
  );
}
